// src/views/adminView/contractsView/compliance/useCarteraCompliance.js
// ─────────────────────────────────────────────────────────────────────────────
// Fuente ÚNICA del semáforo de cumplimiento de la cartera. Fetchea la cartera
// COMPLETA una vez por montaje del hub y en cada `reload()` (paginando hasta totalPages con tope
// defensivo, ver contractsFetchAll.js) y expone resúmenes/KPIs reactivos a las
// mutaciones del store front-only (useComplianceVersion). Lo consumen el badge
// del tab Cumplimiento del hub, el Stat "Carpeta al día" del Directorio y
// CumplimientoCarteraPanel — así los tres muestran el MISMO universo (antes el
// badge se calculaba sobre los 12 contratos del scroll infinito y el panel
// sobre su propio fetch de 100 → números incoherentes).
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAllContractPages } from '../contractsFetchAll';
import { resumenCartera, resumenDeContract } from './complianceUtils';
import { readComplianceStored, useComplianceVersion } from './useComplianceStore';

/**
 * @returns {{
 *  contracts: Array, resumenes: Array, cartera: Object,
 *  totalCartera: number, truncado: boolean, loading: boolean,
 *  error: boolean, version: number, reload: () => void,
 * }}
 */
export function useCarteraCompliance() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [truncado, setTruncado] = useState(false);
  // Cada `reload()` vuelve a traer la cartera (p. ej. tras renovar un contrato: cambian su
  // vigencia y su semáforo, que el modo cartera y el Stat "Carpeta al día" leen de acá).
  const [reloadToken, setReloadToken] = useState(0);

  // Bump en cada write del store (drawer, expediente del detalle…): fuerza el
  // recálculo de los resúmenes aunque `contracts` no cambie.
  const version = useComplianceVersion();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const result = await fetchAllContractPages();
      if (cancelled) return;
      setContracts(result.docs);
      setError(result.error);
      setTruncado(result.truncated);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  const resumenes = useMemo(
    () => contracts.map((c) => resumenDeContract(c, readComplianceStored(c._id))),
    // `version` no se lee en el cuerpo: solo invalida el memo tras cada write.
    [contracts, version], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const cartera = useMemo(() => resumenCartera(resumenes), [resumenes]);

  return {
    contracts,
    resumenes,
    cartera,
    totalCartera: contracts.length,
    truncado,
    loading,
    error,
    version,
    reload,
  };
}

export default useCarteraCompliance;
